/**
 * Client-side detection for edge-middleware + upstream rate limits.
 *
 * Two rate-limit signals flow through the app's fetch:
 *
 *   1. Our own middleware (SDK gateway, rpc-proxy, DDoS backstop).
 *      Tagged with `x-rate-limit-source: middleware`. The interceptor
 *      throws `MiddlewareRateLimitError` and the global retry policy
 *      does zero retries — the next refetch tick handles recovery.
 *
 *   2. Upstream services (envio, shared RPC, etc.) bubbled through
 *      our routes. Tagged with `X-Upstream-Rate-Limited: 1` plus an
 *      upstream `Retry-After`. The interceptor throws
 *      `UpstreamRateLimitResponseError` so the retry policy can
 *      again short-circuit to zero retries (retrying an upstream 429
 *      amplifies the problem). `placeholderData: (prev) => prev` in
 *      `query-provider.tsx` keeps stale data on screen while we wait
 *      for the next poll tick informed by `Retry-After`.
 *
 * Vanilla 429s with neither header are still treated as rate-limit-
 * like (`isUpstreamRateLimit` in the retry policy) but also retry
 * zero times — the design choice per the Apr 30 triage is to always
 * defer to the next refetch tick rather than retry amplify.
 */

export const MIDDLEWARE_RATE_LIMIT_HEADER = 'x-rate-limit-source';
export const MIDDLEWARE_RATE_LIMIT_VALUE = 'middleware';

export const UPSTREAM_RATE_LIMIT_HEADER = 'x-upstream-rate-limited';
export const UPSTREAM_RATE_LIMIT_VALUE = '1';

export class MiddlewareRateLimitError extends Error {
  readonly status: number;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, retryAfterSeconds: number | null) {
    super(
      `Rate limited by middleware (${status})${retryAfterSeconds != null ? ` — retry after ${retryAfterSeconds}s` : ''}`,
    );
    this.name = 'MiddlewareRateLimitError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Thrown when an API route forwards a 429 tagged as an upstream
 * rate limit (`X-Upstream-Rate-Limited: 1`). The retry policy
 * short-circuits on this so we don't amplify backpressure — the
 * next `refetchInterval` tick picks the data up cleanly.
 */
export class UpstreamRateLimitResponseError extends Error {
  readonly status: number;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, retryAfterSeconds: number | null) {
    super(
      `Rate limited by upstream (${status})${retryAfterSeconds != null ? ` — retry after ${retryAfterSeconds}s` : ''}`,
    );
    this.name = 'UpstreamRateLimitResponseError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function isMiddlewareRateLimitResponse(res: Response): boolean {
  if (res.status !== 429) return false;
  return res.headers.get(MIDDLEWARE_RATE_LIMIT_HEADER) === MIDDLEWARE_RATE_LIMIT_VALUE;
}

export function isUpstreamRateLimitResponse(res: Response): boolean {
  if (res.status !== 429) return false;
  return res.headers.get(UPSTREAM_RATE_LIMIT_HEADER) === UPSTREAM_RATE_LIMIT_VALUE;
}

export function parseRetryAfter(value: string | null): number | null {
  if (!value) return null;
  const seconds = Number.parseInt(value, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

const INSTALLED_FLAG = '__takeover_mw_rate_limit_installed__';

type FetchLike = typeof globalThis.fetch;

interface MarkedGlobal {
  [INSTALLED_FLAG]?: boolean;
}

/**
 * Dev-only per-endpoint request counter. Tracks fetch volume per
 * pathname over a 10s rolling window and `console.warn`s when any
 * endpoint exceeds `DEV_WARN_THRESHOLD`. Pure observability — never
 * blocks the request — and only runs when `NODE_ENV !== 'production'`.
 *
 * Catches the four self-inflicted spam loops we shipped to staging
 * before the user noticed: `recordVerifications` re-fire,
 * `usePushNotifications` re-fire, the duplicated notification-bell
 * preferences fetch, and the avatar-img cascade. Any future loop
 * that fires the same endpoint >20×/10s will surface in the dev
 * console immediately instead of via a wall of 429s in production.
 */
const DEV_WARN_THRESHOLD = 20;
const DEV_WINDOW_MS = 10_000;
const devEndpointCounts = new Map<string, number[]>();

function normalizePathnameForDevTracking(input: RequestInfo | URL): string {
  try {
    let url: URL;
    if (typeof input === 'string') {
      // Same-origin URLs may be relative — anchor them so URL()
      // doesn't throw. The fake origin is dropped before display.
      url = new URL(input, 'http://localhost');
    } else if (input instanceof URL) {
      url = input;
    } else {
      url = new URL(input.url, 'http://localhost');
    }
    // Strip query string so the same endpoint with different
    // params (e.g. `/api/users/0xA…` vs `0xB…`) collapses to one
    // bucket — those are the loops we care about catching.
    return url.pathname;
  } catch {
    return '<unparseable-url>';
  }
}

function recordDevRequest(pathname: string): void {
  const now = Date.now();
  const cutoff = now - DEV_WINDOW_MS;
  const existing = devEndpointCounts.get(pathname) ?? [];
  // Drop samples that have rolled out of the window. O(N) but N is
  // tiny in the dev path and we'd rather pay this than carry a more
  // complex ring-buffer just for a console warning.
  const fresh = existing.filter((t) => t > cutoff);
  fresh.push(now);
  devEndpointCounts.set(pathname, fresh);

  if (fresh.length === DEV_WARN_THRESHOLD) {
    console.warn(
      `[dev-rate-watch] ${pathname} fired ${fresh.length}× in the last ${DEV_WINDOW_MS / 1000}s — likely a re-fire loop. Audit the calling effect / observer.`,
    );
  }
}

/**
 * Idempotently patches `window.fetch` to throw
 * `MiddlewareRateLimitError` on responses tagged by our edge
 * middleware. Safe to call multiple times — only the first call
 * installs.
 *
 * Server-side (no `window`) it's a no-op so SSR fetches are
 * unaffected; the middleware never sees same-process Next.js
 * server fetches anyway.
 */
export function installMiddlewareRateLimitInterceptor(): void {
  if (typeof window === 'undefined') return;
  const w = window as MarkedGlobal & Window & typeof globalThis;
  if (w[INSTALLED_FLAG]) return;
  w[INSTALLED_FLAG] = true;

  const originalFetch: FetchLike = window.fetch.bind(window);
  const enableDevWatch = process.env.NODE_ENV !== 'production';

  window.fetch = (async (input, init) => {
    if (enableDevWatch && input != null) {
      recordDevRequest(normalizePathnameForDevTracking(input));
    }
    const response = await originalFetch(input, init);
    if (isMiddlewareRateLimitResponse(response)) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      throw new MiddlewareRateLimitError(response.status, retryAfter);
    }
    if (isUpstreamRateLimitResponse(response)) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      throw new UpstreamRateLimitResponseError(response.status, retryAfter);
    }
    return response;
  }) as FetchLike;
}

/** Test-only: lets specs assert / reset the dev counter. */
export function __resetDevRequestCounts(): void {
  devEndpointCounts.clear();
}

/** Test-only: read the current per-endpoint sample list. */
export function __peekDevRequestCounts(pathname: string): number[] {
  return [...(devEndpointCounts.get(pathname) ?? [])];
}
